import type { Problem } from "./types";

/** An error answer from the API (problem+json), or a network failure (status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Messages keyed by request field, for showing next to inputs. */
  readonly fields: Readonly<Record<string, string>>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  static fromProblem(status: number, problem: Partial<Problem> | undefined): ApiError {
    const fields: Record<string, string> = {};
    for (const item of problem?.errors ?? []) fields[item.field] = item.message;
    return new ApiError(
      status,
      problem?.code ?? "unknown_error",
      problem?.detail ?? problem?.title ?? "Something went wrong. Try again.",
      fields,
    );
  }

  static network(): ApiError {
    return new ApiError(0, "network_error", "Cannot reach Zotula. Check your connection and try again.");
  }
}

/** A message that is safe to show for any thrown value. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Try again.";
}

/** The message for one field, when the API pointed at it. */
export function fieldError(error: unknown, field: string): string | undefined {
  return error instanceof ApiError ? error.fields[field] : undefined;
}
